import { useCallback, useEffect, useMemo, useState } from "react";
import { SURGICAL_ACT_LIMITS } from "../../../classes/constants/surgicalActConstants";
import { usePlanContext } from "../../../classes/contexts/PlanContext";
import { EventsEnum } from "../../../classes/enums/EventsEnum";
import { MedicalImagesType } from "../../../classes/enums/MedicalImagesEnum";
import {
  ActNatures,
  isPCOType,
  OsteotomyTypes,
} from "../../../classes/enums/SurgicalActsEnum";
import { VertebraSelectionModality } from "../../../classes/enums/VertebraSelectionEnum";
import { vertebraUtils } from "../../../classes/utils/AnnotationsUtils";
import { surgeryUtils } from "../../../classes/utils/SurgeryUtils";
import Button from "../../../components/button/Button";
import IncrementDecrementButtons from "../../../components/incrementDecrementButtons/IncrementDecrementButtons";
import LabeledSlider from "../../../components/labeledSlider/LabeledSlider";
import MeasurementDisplay from "../../../components/measurementDisplay/MeasurementDisplay";
import LabeledRangeSelect from "../../../components/select/LabeledRangeSelect";
import LabeledSelect from "../../../components/select/LabeledSelect";
import useSurgicalActActions from "../../../hooks/useSurgicalActActions";
import "./AlignPopupParameterSelector.css";

const getInitialMaxAngle = type => {
  const limits = SURGICAL_ACT_LIMITS.OSTEOTOMY;
  if (type === OsteotomyTypes.GRADE_1_PCO) return limits.GRADE_1_MAX;
  if (type === OsteotomyTypes.GRADE_2_PCO) return limits.GRADE_2_MAX;
  return limits.GRADE_3_MAX; // covers III, IV, and default (null)
};

const Osteotomy = ({
  isEditMode = false,
  existingAct = null,
  onConfirm = null,
  onRequiredFieldsChange = null,
  hasL6 = false,
  hasT13 = false,
}) => {
  const { currentPlanId } = usePlanContext();

  const {
    startVertebraSelection,
    stopVertebraSelection,
    showOsteotomyTool,
    hideOsteotomyTool,
    updateOsteotomyToolValue,
    getOsteotomySupportValues,
  } = useSurgicalActActions(currentPlanId);

  const [selectedOsteotomyType, setSelectedOsteotomyType] = useState(null);
  const [selectedVertebrae, setSelectedVertebrae] = useState([]);
  const [currentAngle, setCurrentAngle] = useState(0);
  const [maxAngle, setMaxAngle] = useState(() =>
    getInitialMaxAngle(null)
  );

  const [isVertebraSelectionActive, setIsVertebraSelectionActive] =
    useState(false);
  const [isOsteotomyToolActive, setIsOsteotomyToolActive] = useState(false);
  const [isLoadingOsteotomyTool, setIsLoadingOsteotomyTool] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const vertebraItems = useMemo(() => {
    const isGrade2PcoOsteotomy =
      selectedOsteotomyType === OsteotomyTypes.GRADE_2_PCO;

    const baseVertebrae = [
      "-",
      ...vertebraUtils.getAvailableVertebrae(hasL6, hasT13),
    ];

    return baseVertebrae.map(vertebra => ({
      value: vertebra,
      label:
        isGrade2PcoOsteotomy
          ? vertebraUtils.getGrade2PcoLabel(vertebra, hasL6, hasT13)
          : vertebra === "-"
          ? "-"
          : vertebraUtils.getVertebraLabel(vertebra),
    }));
  }, [selectedOsteotomyType, hasL6, hasT13]);

  const osteotomyTypes = useMemo(() => {
    const allTypes = Object.values(OsteotomyTypes).map(type => ({
      value: type,
      label: type,
    }));

    const typesWithDefault = [{ value: "", label: "-" }, ...allTypes];

    if (!isEditMode) return typesWithDefault;

    if (selectedOsteotomyType && isPCOType(selectedOsteotomyType)) {
      return [
        { value: "", label: "-" },
        {
          value: OsteotomyTypes.GRADE_1_PCO,
          label: OsteotomyTypes.GRADE_1_PCO,
        },
        {
          value: OsteotomyTypes.GRADE_2_PCO,
          label: OsteotomyTypes.GRADE_2_PCO,
        },
      ];
    }

    return typesWithDefault;
  }, [selectedOsteotomyType, isEditMode]);

  const selectedVertebraItem = useMemo(() => {
    const firstVertebra =
      selectedVertebrae.length > 0 ? selectedVertebrae[0] : null;

    if (!firstVertebra || firstVertebra === "-") {
      return vertebraItems[0];
    }

    const foundItem = vertebraItems.find(item => item.value === firstVertebra);

    return foundItem || vertebraItems[0];
  }, [selectedVertebrae, vertebraItems]);

  const lastSelectedVertebraItem = useMemo(() => {
    const lastVertebra =
      selectedVertebrae.length > 1
        ? selectedVertebrae[selectedVertebrae.length - 1]
        : null;
    if (!lastVertebra || lastVertebra === "-") {
      return vertebraItems[0];
    }
    const foundItem = vertebraItems.find(item => item.value === lastVertebra);
    return foundItem || vertebraItems[0];
  }, [selectedVertebrae, vertebraItems]);

  const selectedOsteotomyTypeItem = useMemo(() => {
    if (!selectedOsteotomyType || selectedOsteotomyType === null) {
      return osteotomyTypes[0];
    }

    const foundItem = osteotomyTypes.find(
      item => item.value === selectedOsteotomyType
    );
    return foundItem || osteotomyTypes[0];
  }, [selectedOsteotomyType, osteotomyTypes]);

  const getVertebraId = useCallback(vertebra => {
    if (!vertebra || vertebra === "-") return null;
    return vertebraUtils.getCentroidEntity(vertebra);
  }, []);

  const isConfirmDisabled = useMemo(() => {
    const hasValidVertebra =
      selectedVertebrae.length > 0 &&
      selectedVertebrae[0] &&
      selectedVertebrae[0] !== "-";
    const hasValidOsteotomyType =
      selectedOsteotomyType && selectedOsteotomyType !== null;
    const hasValidAngle = currentAngle >= 0 && currentAngle <= maxAngle;

    return (
      !hasValidVertebra ||
      !hasValidOsteotomyType ||
      !hasValidAngle ||
      isLoadingOsteotomyTool ||
      isConfirming
    );
  }, [
    selectedVertebrae,
    selectedOsteotomyType,
    currentAngle,
    maxAngle,
    isLoadingOsteotomyTool,
    isConfirming,
  ]);

  // Check if required fields are filled and notify parent
  useEffect(() => {
    if (onRequiredFieldsChange) {
      const hasValidVertebra =
        selectedVertebrae.length > 0 &&
        selectedVertebrae[0] &&
        selectedVertebrae[0] !== "-";
      const hasValidOsteotomyType =
        selectedOsteotomyType && selectedOsteotomyType !== null;

      const hasRequiredFields = hasValidVertebra && hasValidOsteotomyType;
      onRequiredFieldsChange("OSTEOTOMY", hasRequiredFields);
    }
  }, [selectedVertebrae, selectedOsteotomyType, onRequiredFieldsChange]);

  useEffect(() => {
    if (isEditMode && existingAct) {
      if (existingAct.vertebrae && existingAct.vertebrae.length > 0) {
        setSelectedVertebrae(existingAct.vertebrae);
      } else if (existingAct.vertebra) {
        setSelectedVertebrae([existingAct.vertebra]);
      }

      if (existingAct.osteotomyType) {
        setSelectedOsteotomyType(existingAct.osteotomyType);
      }

      if (existingAct.value !== undefined) {
        setCurrentAngle(existingAct.value);
      }

      if (isVertebraSelectionActive) {
        stopVertebraSelection();
        setIsVertebraSelectionActive(false);
      }
    }
  }, [
    isEditMode,
    existingAct,
    isVertebraSelectionActive,
    stopVertebraSelection,
  ]);

  useEffect(() => {
    startVertebraSelection(
      MedicalImagesType.LATERAL_SURGERY_DX,
      VertebraSelectionModality.OSTEOTOMY,
      1
    );
    setIsVertebraSelectionActive(true);
  }, [startVertebraSelection]);

  useEffect(() => {
    return () => {
      if (isVertebraSelectionActive) {
        stopVertebraSelection();
      }
      if (isOsteotomyToolActive) {
        hideOsteotomyTool();
      }
    };
  }, [
    stopVertebraSelection,
    isVertebraSelectionActive,
    isOsteotomyToolActive,
    hideOsteotomyTool,
  ]);

  const handleVertebraSelected = useCallback(event => {
    const tag = event.detail[0].tag;
    if (!tag || tag !== VertebraSelectionModality.OSTEOTOMY) {
      return;
    }

    const vertebraId = event.detail[0].id;
    const vertebraType = vertebraUtils.getVertebraFromId(vertebraId);

    setSelectedVertebrae([vertebraType]);

    const nature = event.detail[0].attributes.nature;
    const typeFromNature =
      surgeryUtils.mapLandmarkNatureToOsteotomyType(nature);

    setSelectedOsteotomyType(typeFromNature);
  }, []);

  const handleManualVertebraChange = useCallback(
    selectedItem => {
      if (isVertebraSelectionActive) {
        stopVertebraSelection();
        setIsVertebraSelectionActive(false);
      }

      const value = selectedItem.target
        ? selectedItem.target.value
        : selectedItem.value;
      setSelectedVertebrae(value === "-" ? [] : [value]);
    },
    [isVertebraSelectionActive, stopVertebraSelection]
  );

  const handleManualSeconVertebraChange = useCallback(
    selectedItem => {
      const value = selectedItem.target
        ? selectedItem.target.value
        : selectedItem.value;
      if (value === "-") {
        setSelectedVertebrae([]);
        return;
      }

      const firstVertebra = selectedVertebrae[0];
      if (!firstVertebra || firstVertebra === "-") {
        setSelectedVertebrae([]);
        return;
      }

      const firstIndex = vertebraItems.findIndex(
        item => item.value === firstVertebra
      );
      const secondIndex = vertebraItems.findIndex(item => item.value === value);

      if (firstIndex === -1 || secondIndex === -1) {
        setSelectedVertebrae([]);
        return;
      }

      const startIndex = Math.min(firstIndex, secondIndex);
      const endIndex = Math.max(firstIndex, secondIndex);

      const vertebraRange = vertebraItems
        .slice(startIndex, endIndex + 1)
        .map(item => item.value)
        .filter(vertebra => vertebra !== "-");

      const finalRange =
        secondIndex < firstIndex ? vertebraRange.reverse() : vertebraRange;

      setSelectedVertebrae(finalRange);
    },
    [selectedVertebrae, vertebraItems]
  );

  const handleOsteotomyTypeChange = useCallback(selectedItem => {
    const value = selectedItem.target
      ? selectedItem.target.value
      : selectedItem.value;
    setSelectedOsteotomyType(value === "" || value === null ? null : value);
    setSelectedVertebrae(prev => (isPCOType(value) ? prev : prev.slice(0, 1)));
  }, []);

  useEffect(() => {
    setMaxAngle(getInitialMaxAngle(selectedOsteotomyType));
  }, [selectedOsteotomyType]);

  const handleAngleChange = useCallback(
    async newAngle => {
      const numericAngle = Number(newAngle);
      setCurrentAngle(numericAngle);

      if (
        selectedVertebrae.length > 0 &&
        selectedVertebrae[0] &&
        selectedVertebrae[0] !== "-"
      ) {
        const vertebraId = getVertebraId(selectedVertebrae[0]);
        if (vertebraId) {
          const response = await updateOsteotomyToolValue(
            vertebraId,
            numericAngle
          );

          if (response && response.max_angle) {
            setMaxAngle(Math.round(response.max_angle));
          }
        }
      }
    },
    [selectedVertebrae, getVertebraId, updateOsteotomyToolValue]
  );

  const handleIncrement = useCallback(async () => {
    const newAngle = Math.min(currentAngle + 1, maxAngle);
    setCurrentAngle(newAngle);

    if (
      selectedVertebrae.length > 0 &&
      selectedVertebrae[0] &&
      selectedVertebrae[0] !== "-"
    ) {
      const vertebraId = getVertebraId(selectedVertebrae[0]);
      if (vertebraId) {
        const response = await updateOsteotomyToolValue(vertebraId, newAngle);

        if (response && response.max_angle) {
          setMaxAngle(Math.round(response.max_angle));
        }
      }
    }
  }, [
    currentAngle,
    maxAngle,
    selectedVertebrae,
    getVertebraId,
    updateOsteotomyToolValue,
  ]);

  const handleDecrement = useCallback(async () => {
    const newAngle = Math.max(currentAngle - 1, 0);
    setCurrentAngle(newAngle);

    if (
      selectedVertebrae.length > 0 &&
      selectedVertebrae[0] &&
      selectedVertebrae[0] !== "-"
    ) {
      const vertebraId = getVertebraId(selectedVertebrae[0]);
      if (vertebraId) {
        const response = await updateOsteotomyToolValue(vertebraId, newAngle);

        if (response && response.max_angle) {
          setMaxAngle(Math.round(response.max_angle));
        }
      }
    }
  }, [
    currentAngle,
    selectedVertebrae,
    getVertebraId,
    updateOsteotomyToolValue,
  ]);

  const handleManualOsteotomyToolUpdate = useCallback(event => {
    const updateData = event.detail[0];
    if (updateData) {
      if (updateData.current_value !== undefined) {
        setCurrentAngle(Math.round(updateData.current_value));
      }

      if (updateData.max_value !== undefined) {
        setMaxAngle(Math.round(updateData.max_value));
      }
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    if (isConfirming) return;

    if (
      selectedVertebrae.length === 0 ||
      !selectedVertebrae[0] ||
      selectedVertebrae[0] === "-"
    ) {
      return;
    }

    if (!selectedOsteotomyType) {
      return;
    }

    if (currentAngle < 0 || currentAngle > maxAngle) {
      return;
    }

    setIsConfirming(true);

    try {
      const isPCO = isPCOType(selectedOsteotomyType);
      const osteotomyActs = [];

      if (isPCO && selectedVertebrae.length > 1) {
        const firstVertebraId = getVertebraId(selectedVertebrae[0]);
        let supportValues = null;
        if (firstVertebraId) {
          try {
            supportValues = await getOsteotomySupportValues(firstVertebraId);
          } catch (error) {}
        }

        for (const vertebra of selectedVertebrae) {
          const osteotomyAct = {
            actNature: ActNatures.Osteotomy,
            vertebra: vertebra,
            vertebrae: [vertebra],
            value: currentAngle,
            osteotomyType: selectedOsteotomyType,
            supportPoints: supportValues,
          };

          osteotomyActs.push(osteotomyAct);
        }
      } else {
        const vertebraId = getVertebraId(selectedVertebrae[0]);

        let supportValues = null;
        if (vertebraId) {
          try {
            supportValues = await getOsteotomySupportValues(vertebraId);
          } catch (error) {}
        }

        const osteotomyAct = {
          actNature: ActNatures.Osteotomy,
          vertebra: selectedVertebrae[0],
          vertebrae: selectedVertebrae,
          value: currentAngle,
          osteotomyType: selectedOsteotomyType,
          supportPoints: supportValues,
        };

        osteotomyActs.push(osteotomyAct);
      }

      if (onConfirm) {
        onConfirm(osteotomyActs);
      } else {
      }
    } catch (error) {
    } finally {
      setIsConfirming(false);
    }
  }, [
    selectedVertebrae,
    selectedOsteotomyType,
    currentAngle,
    maxAngle,
    getVertebraId,
    getOsteotomySupportValues,
    isConfirming,
    onConfirm,
  ]);

  useEffect(() => {
    window.addEventListener(
      EventsEnum.VERTEBRA_SELECTION,
      handleVertebraSelected
    );
    return () => {
      window.removeEventListener(
        EventsEnum.VERTEBRA_SELECTION,
        handleVertebraSelected
      );
    };
  }, [handleVertebraSelected]);

  useEffect(() => {
    window.addEventListener(
      EventsEnum.MANUAL_OSTEOTOMY_TOOL_UPDATE,
      handleManualOsteotomyToolUpdate
    );
    return () => {
      window.removeEventListener(
        EventsEnum.MANUAL_OSTEOTOMY_TOOL_UPDATE,
        handleManualOsteotomyToolUpdate
      );
    };
  }, [handleManualOsteotomyToolUpdate]);

  useEffect(() => {
    let timeoutId = null;

    if (
      selectedVertebrae.length > 0 &&
      selectedVertebrae[0] &&
      selectedVertebrae[0] !== "-" &&
      selectedOsteotomyType
    ) {
      const vertebraId = getVertebraId(selectedVertebrae[0]);

      if (vertebraId) {
        timeoutId = setTimeout(async () => {
          setIsLoadingOsteotomyTool(true);

          try {
            const vertebraIds = selectedVertebrae
              .map(vertebra => getVertebraId(vertebra))
              .filter(id => id !== null);

            const osteotomyParameters = await showOsteotomyTool(
              vertebraIds,
              selectedOsteotomyType
            );

            if (osteotomyParameters) {
              setCurrentAngle(
                Math.round(osteotomyParameters.current_angle || 0)
              );
              setMaxAngle(Math.round(osteotomyParameters.max_angle || 10));
            }

            setIsOsteotomyToolActive(true);
          } catch (error) {
          } finally {
            setIsLoadingOsteotomyTool(false);
          }
        }, 100);
      }
    } else {
      if (isOsteotomyToolActive) {
        hideOsteotomyTool();
        setIsOsteotomyToolActive(false);
        setIsLoadingOsteotomyTool(false);
        setCurrentAngle(0);
        setMaxAngle(getInitialMaxAngle(null));
        setSelectedOsteotomyType(null);
        setSelectedVertebrae([]);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    selectedVertebrae,
    selectedOsteotomyType,
    getVertebraId,
    showOsteotomyTool,
    isOsteotomyToolActive,
    hideOsteotomyTool,
  ]);

  return (
    <div className="AlignPopupParameterSelector-container">
      <div className="AlignPopupParameterSelector-scrollable-content">
        <div className="AlignPopupParameterSelector-controls-wrapper">
          <div className="AlignPopupParameterSelector-header">
            {(selectedVertebrae.length === 0 ||
              !selectedVertebrae[0] ||
              selectedVertebrae[0] === "-") &&
              isVertebraSelectionActive && (
                <div className="App-text-medium AlignPopupParameterSelector-subtitle">
                  Select a point on the image
                </div>
              )}
          </div>

          <LabeledSelect
            label="Type"
            items={osteotomyTypes}
            selectedItem={selectedOsteotomyTypeItem}
            onChange={handleOsteotomyTypeChange}
          />

          <LabeledRangeSelect
            label="Vertebra"
            firstItems={vertebraItems}
            secondItems={vertebraItems.filter(
              item => item.value !== selectedVertebraItem.value
            )}
            firstSelectedItem={selectedVertebraItem}
            secondSelectedItem={lastSelectedVertebraItem}
            onChangeFirst={handleManualVertebraChange}
            onChangeSecond={handleManualSeconVertebraChange}
            hasOnlyOneItem={
              !selectedOsteotomyType || !isPCOType(selectedOsteotomyType)
            }
          />

          <LabeledSlider
            label="Angle"
            min={0}
            max={maxAngle}
            value={currentAngle}
            onChange={handleAngleChange}
            disabled={
              !selectedOsteotomyType ||
              selectedOsteotomyType === null ||
              isLoadingOsteotomyTool ||
              isConfirming
            }
          />
          <div className="AlignPopupParameterSelector-measurement-container">
            <MeasurementDisplay
              value={currentAngle}
              unit="°"
              disabled={
                !selectedOsteotomyType ||
                selectedOsteotomyType === null ||
                isLoadingOsteotomyTool ||
                isConfirming
              }
            />
            <IncrementDecrementButtons
              onIncrement={handleIncrement}
              onDecrement={handleDecrement}
              onIncrementEnd={() => {}}
              onDecrementEnd={() => {}}
              disabled={
                !selectedOsteotomyType ||
                selectedOsteotomyType === null ||
                isLoadingOsteotomyTool ||
                isConfirming
              }
            />
          </div>
        </div>
      </div>

      <div className="AlignPopupParameterSelector-button-wrapper">
        <Button
          value={isConfirming ? "Confirming..." : "Confirm"}
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        />
      </div>
    </div>
  );
};

export default Osteotomy;
